import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import MainGrid from '../components/MainGrid';
import Dropzone from 'react-dropzone';
import { ERROR_STATE, ExternalAPIType } from '../Utils/Enums';
import {
	getAccessToken,
	getElderHomeId,
	getUserEmail,
	getUserId,
	getUserName,
} from '../Utils/AuthUtil';
import { useEffect, useState } from 'react';
import { sendClientApiDetails } from '../services/CarePlanServices';
import { toast } from 'react-toastify';
import { storeExternalApiConfig } from '../services/ElderService';
import { helperText } from '../assets/theme/Theme';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { fetchCareHome, fetchCareHomeConfigs } from '../services/UserService';
import ToastBanner from '../components/ToastBanner';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { dark, teal } from '../assets/theme/Colors';

const integrations = [
	{
		name: 'Care plan api',
		status: 'Connected',
		color: '#16a34a',
		bg: '#dcfce7',
	},
	{
		name: 'Resident api',
		status: 'Connected',
		color: '#16a34a',
		bg: '#dcfce7',
	},
];

const API_INITIAL_STATE = {
	apiName: '',
	endpoint: '',
	method: '',
	authType: '',
	token: '',
	rootField: '',
	uniqueKey: '',
	fields: [
		{ key: 'elderId', value: '' },
		{ key: 'name', value: '' },
	],
	headers: [{ key: '', value: '' }],
	queryParams: [{ key: '', value: '' }],
	requestBody: '',
};

const CURL_API_INITIAL_STATE = {
	rootField: '',
	idKeyField: '',
	requestBody: '',
};

const API_ERROR_STATE = {
	apiName: ERROR_STATE,
	endpoint: ERROR_STATE,
	method: ERROR_STATE,
	authType: ERROR_STATE,
	token: ERROR_STATE,
	rootField: ERROR_STATE,
	uniqueKey: ERROR_STATE,
	fields: ERROR_STATE,
	headers: ERROR_STATE,
	queryParams: ERROR_STATE,
};

const CURL_API_ERROR_STATE = {
	rootField: ERROR_STATE,
	idKeyField: ERROR_STATE,
	requestBody: ERROR_STATE,
};

const METHODS = ['GET', 'POST'];

const AUTH_TYPES = ['NONE', 'BEARER', 'BASIC', 'API_KEY', 'API_KEY_QUERY'];

const INITIAL_ORG = {
	name: '',
	cqcNumber: '',
	address: '',
	adminEmail: '',
	primaryContact: '',
};

function FormField({ label, placeholder, value, onChange }) {
	return (
		<Box>
			<Typography
				sx={{
					fontSize: 12,
					fontWeight: 500,
					mb: 1,
					color: '#334155',
				}}>
				{label}
			</Typography>

			<TextField
				fullWidth
				size="small"
				onChange={onChange}
				value={value}
				placeholder={placeholder}
				sx={{
					'& .MuiInputBase-input': {
						fontSize: 13,
					},
				}}
				InputProps={{
					readOnly: true,
				}}
			/>
		</Box>
	);
}

export default function Settings() {
	const token = getAccessToken();
	const userId = getUserId();
	const homeId = getElderHomeId();
	const [fileData, setFileData] = useState('');
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [isStoringReApiConfig, setIsStoringReApiConfig] = useState(false);
	const [apiTypeTab, setApiTypeTab] = useState('Manual Setup');
	const [manualApiData, setManualApiData] = useState(API_INITIAL_STATE);
	const [manualApiErrors, setManualApiError] = useState(API_ERROR_STATE);
	const [cUrlCommand, setCurlCommand] = useState(CURL_API_INITIAL_STATE);
	const [commandError, setCommandError] = useState(CURL_API_ERROR_STATE);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [configs, setConfigs] = useState([]);
	const [orgData, setOrgData] = useState(INITIAL_ORG);

	useEffect(() => {
		fetchConfigs();
		fetchHome();
	}, []);

	async function fetchConfigs() {
		const response = await fetchCareHomeConfigs(homeId, token);

		if (response.message === 'Success') {
			setConfigs(response.content);
		} else {
			console.error(response.message);
		}
	}

	async function fetchHome() {
		const response = await fetchCareHome(homeId, token);

		if (response.message === 'Success') {
			const home = response.content;

			setOrgData(prevState => ({
				...prevState,
				name: home.name !== 'N/A' ? home.name : 'SenzCare Home',
				adminEmail: getUserEmail(),
				primaryContact: getUserName(),
				address: home.location,
			}));
		} else {
			console.error(response.message);
		}
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['json'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function validateManualApiInputs() {
		let isValid = true;

		if (!manualApiData.apiName || manualApiData.apiName === '') {
			setManualApiError(prevState => ({
				...prevState,
				apiName: {
					status: true,
					msg: 'Enter api name',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.authType || manualApiData.authType === '') {
			setManualApiError(prevState => ({
				...prevState,
				authType: {
					status: true,
					msg: 'Select auth type',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.endpoint || manualApiData.endpoint === '') {
			setManualApiError(prevState => ({
				...prevState,
				endpoint: {
					status: true,
					msg: 'Enter endpoint',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.method || manualApiData.method === '') {
			setManualApiError(prevState => ({
				...prevState,
				method: {
					status: true,
					msg: 'Select api method',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.token || manualApiData.token === '') {
			setManualApiError(prevState => ({
				...prevState,
				token: {
					status: true,
					msg: 'Enter api token',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.rootField || manualApiData.rootField === '') {
			setManualApiError(prevState => ({
				...prevState,
				rootField: {
					status: true,
					msg: 'Enter list path for request mapping',
				},
			}));
			isValid = false;
		}

		if (!manualApiData.uniqueKey || manualApiData.uniqueKey === '') {
			setManualApiError(prevState => ({
				...prevState,
				uniqueKey: {
					status: true,
					msg: 'Enter unique key for request mapping',
				},
			}));
			isValid = false;
		}

		if (
			!manualApiData.fields ||
			manualApiData.fields.some(f => !f.value?.trim())
		) {
			setManualApiError(prevState => ({
				...prevState,
				fields: {
					status: true,
					msg: 'Map the fields for request mapping',
				},
			}));
			isValid = false;
		}

		if (manualApiData.headers.length === 0) {
			setManualApiError(prevState => ({
				...prevState,
				headers: {
					status: true,
					msg: 'Enter api headers',
				},
			}));
			isValid = false;
		}

		if (manualApiData.queryParams.length === 0) {
			setManualApiError(prevState => ({
				...prevState,
				queryParams: {
					status: true,
					msg: 'Enter api query parameters',
				},
			}));
			isValid = false;
		}

		return isValid;
	}

	function validateCommandApiInputs() {
		let isValid = true;

		if (!cUrlCommand.idKeyField || cUrlCommand.idKeyField === '') {
			setCommandError(prevState => ({
				...prevState,
				idKeyField: {
					status: true,
					msg: 'Enter id key id in api response',
				},
			}));
			isValid = false;
		}

		if (!cUrlCommand.rootField || cUrlCommand.rootField === '') {
			setCommandError(prevState => ({
				...prevState,
				rootField: {
					status: true,
					msg: 'Enter root field in api response',
				},
			}));
			isValid = false;
		}

		if (!cUrlCommand.requestBody || cUrlCommand.requestBody === '') {
			setCommandError(prevState => ({
				...prevState,
				requestBody: {
					status: true,
					msg: 'Enter cUrl command',
				},
			}));
			isValid = false;
		}

		return isValid;
	}

	async function onUploadJsonFile() {
		if (!fileData) {
			setTxtError({ status: true, msg: 'Select json file' });
			return;
		}

		setIsLoading(true);
		setLoadingMsg('Uploading configuration');
		const formData = new FormData();
		formData.append('file', fileData);
		formData.append('client_id', getElderHomeId());

		const response = await sendClientApiDetails(formData);

		if (!response.error) {
			onStoreExternalApiDetails();
		} else {
			toast.error(response.message);
			setIsLoading(false);
		}
	}

	async function onStoreExternalApiDetails() {
		if (!fileData) return;

		setIsLoading(true);
		setLoadingMsg('Onboarding Elders');

		const reader = new FileReader();
		reader.onload = event => {
			try {
				const json = JSON.parse(event.target.result);

				if (json.endpoints) {
					const authConfig = json.auth_config;
					const residentApi = json.endpoints.residentApi;
					const careNoteApi = json.endpoints.careNoteApi;
					const carePlanApi = json.endpoints.carePlanApi.find(
						api => api.primary,
					);
					const otherApi = json.endpoints.carePlanApi.filter(
						api => !api.primary,
					);

					onStoreApiConfigs(
						[
							{ data: residentApi, type: ExternalAPIType.reApi },
							{ data: careNoteApi, type: ExternalAPIType.cnApi },
							{ data: carePlanApi, type: ExternalAPIType.cpApi },
							...otherApi.map(api => ({
								data: api,
								type: ExternalAPIType.otApi,
							})),
						],
						authConfig,
					);
				} else {
					alert("JSON does not contain 'endpoints' object");
				}
			} catch (err) {
				alert('Invalid JSON file');
			}
		};
		reader.readAsText(fileData);

		setTimeout(() => {
			setFileData('');
			setIsGenerationComplete(false);
			setIsLoading(false);
		}, 1000);
	}

	// Unused Function
	async function onStoreApiConfigsManual() {
		const isInputsValid = validateManualApiInputs();
		if (!isInputsValid) {
			return;
		}

		setIsStoringReApiConfig(true);

		const formattedHeaders = Object.fromEntries(
			(manualApiData?.headers ?? [])
				.filter(h => h.key && h.value)
				.map(h => [h.key, h.value]),
		);

		const formattedQueryParams = Object.fromEntries(
			(manualApiData?.queryParams ?? [])
				.filter(h => h.key && h.value)
				.map(h => [h.key, h.value]),
		);

		const formattedPathParams = Object.fromEntries(
			(manualApiData?.pathParams ?? [])
				.filter(h => h.key && h.value)
				.map(h => [h.key, h.value]),
		);

		const formattedField = Object.fromEntries(
			(manualApiData?.fields ?? [])
				.filter(h => h.key && h.value)
				.map(h => [h.key, h.value]),
		);

		const apiData = {
			apiName: manualApiData.apiName,
			method: manualApiData.method,
			endpoint: manualApiData.endpoint,
			authType: manualApiData.authType,
			token: manualApiData.token,
			headers: formattedHeaders,
			pathParams: formattedPathParams,
			queryParams: formattedQueryParams,
			requestBody: manualApiData.requestBody,
			rootField: manualApiData.rootField,
			uniqueKey: manualApiData.uniqueKey,
			fields: formattedField,
			authName: 'Authorization',
			authIn: 'header',
		};

		onStoreApiConfigs(apiData, ExternalAPIType.reApi, true);
		setIsStoringReApiConfig(false);
	}

	async function onStoreApiConfigs(
		apiDataList,
		authConfig,
		manualSetup = false,
	) {
		const body = apiDataList.map(apiDataObj => {
			const apiData = apiDataObj.data;

			return {
				name: apiData.name,
				method: apiData.method,
				endpoint: apiData.endpoint,
				authType: authConfig.auth_type,
				token: authConfig.key,
				headers: apiData.headers,
				pathParams: apiData.path_params,
				queryParams: apiData.query_params,
				requestBody: '',
				responseMapping: {
					listPath: apiData.response_mapping.listPath,
					uniqueKey: apiData.response_mapping.uniqueKey,
					fields: apiData.response_mapping.fields,
				},
				sourceType: 'manual',
				elderHomeId: homeId,
				adminId: userId,
				apiType: apiDataObj.type,
				authName: authConfig.name,
				authIn: authConfig.in,
			};
		});

		const response = await storeExternalApiConfig(token, body);
		if (!response.error) {
			if (response.content.externalSyncSuccess) {
				setIsGenerationComplete(true);
				setLoadingMsg('Onboarding Successfully');
				setManualApiData(API_INITIAL_STATE);
				fetchConfigs();
			} else {
				setIsGenerationComplete(false);
				setIsLoading(false);
				toast.error(response.content.externalErrors[0]);
			}
		} else {
			toast.error(response.message);
		}
	}

	function onChangeApiData(value, name) {
		setManualApiError(prevState => ({
			...prevState,
			[name]: ERROR_STATE,
		}));

		setManualApiData(prevState => ({
			...prevState,
			[name]: value,
		}));
	}

	const handleAddNewInput = name => {
		const data = manualApiData[name];
		setManualApiData(prevState => ({
			...prevState,
			[name]: [...data, { key: '', value: '' }],
		}));
	};

	const handleRemoveInput = (name, index) => {
		const updated = manualApiData[name].filter((_, i) => i !== index);
		setManualApiData(prevState => ({
			...prevState,
			[name]: updated,
		}));
	};

	const handleMultiChange = (name, index, field, value) => {
		const updated = manualApiData[name];
		updated[index][field] = value;
		setManualApiData(prevState => ({
			...prevState,
			[name]: updated,
		}));
	};

	function onChangeCurlApiData(value, name) {
		setCommandError(prevState => ({
			...prevState,
			[name]: ERROR_STATE,
		}));

		setCurlCommand(prevState => ({
			...prevState,
			[name]: value,
		}));
	}

	const handleChange = (field, value) => {
		setOrgData(prev => ({
			...prev,
			[field]: value,
		}));
	};

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Settings
					</Typography>

					<Typography
						sx={{
							fontSize: 14,
							color: '#64748b',
						}}>
						Organisation profile and API integration configuration
					</Typography>
				</Box>

				<Grid container spacing={3}>
					{/* Care Home Profile */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								borderRadius: 4,
								border: '1px solid #e2e8f0',
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<ApartmentRoundedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Care Home Profile
									</Typography>
								</Stack>
								<Stack spacing={2.5}>
									<FormField
										label="Organisation Name"
										placeholder="SenzCare Home"
										value={orgData.name}
										onChange={e =>
											handleChange('name', e.target.value)
										}
									/>

									<FormField
										label="CQC Registration Number"
										placeholder="1-2345678901"
										value={orgData.cqcNumber}
										onChange={e =>
											handleChange(
												'cqcNumber',
												e.target.value,
											)
										}
									/>

									<FormField
										label="Address"
										placeholder="12 Meadow Lane, Leeds, LS1 4AB"
										value={orgData.address}
										onChange={e =>
											handleChange(
												'address',
												e.target.value,
											)
										}
									/>

									<FormField
										label="Admin Email"
										placeholder="admin@senzcare.com"
										value={orgData.adminEmail}
										onChange={e =>
											handleChange(
												'adminEmail',
												e.target.value,
											)
										}
									/>

									<FormField
										label="Primary Contact"
										placeholder="Admin"
										value={orgData.primaryContact}
										onChange={e =>
											handleChange(
												'primaryContact',
												e.target.value,
											)
										}
									/>

									{/* <Button
										variant="contained"
										startIcon={<SaveRoundedIcon />}
										sx={{
											width: '40%',
											borderRadius: '12px',
											backgroundColor: teal,
											textTransform: 'none',
											fontWeight: 600,
											fontSize: '13px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: teal,
												boxShadow: 'none',
											},
											color: dark,
										}}>
										Save Profile
									</Button> */}
								</Stack>
							</CardContent>
						</Card>
					</Grid>

					{/* API Integrations */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								borderRadius: 4,
								border: '1px solid #e2e8f0',
								boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
							}}>
							<CardContent sx={{ p: 3 }}>
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<DeviceHubRoundedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										API Integrations
									</Typography>
								</Stack>

								<Stack spacing={1.5}>
									{configs.length > 0 ? (
										integrations.map(item => (
											<Box
												key={item.name}
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent:
														'space-between',
													p: 1.5,
													border: '1px solid #e2e8f0',
													borderRadius: 3,
													backgroundColor: '#fff',
												}}>
												<Stack
													direction="row"
													alignItems="center"
													spacing={1.2}>
													<Box
														sx={{
															width: 10,
															height: 10,
															borderRadius: '50%',
															backgroundColor:
																item.color,
														}}
													/>

													<Typography
														fontSize={13}
														fontWeight={500}>
														{item.name}
													</Typography>
												</Stack>

												<Chip
													label={item.status}
													size="small"
													sx={{
														fontSize: 11,
														fontWeight: 700,
														backgroundColor:
															item.bg,
														color: item.color,
													}}
												/>
											</Box>
										))
									) : (
										<Card
											sx={{
												borderRadius: '18px',
												border: '1px solid #d8e8e8',
												boxShadow: 'none',
												alignItems: 'center',
												width: '100%',
											}}>
											<CardContent
												sx={{
													p: 3,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													minHeight: 50,
													color: '#6b8888',
													fontSize: 13,
													fontWeight: 500,
												}}>
												No APIs connected.
											</CardContent>
										</Card>
									)}
								</Stack>

								<Box sx={{ mt: 3 }}>
									<Typography
										sx={{
											fontSize: 13,
											fontWeight: 700,
											color: '#334155',
											mb: 1.5,
										}}>
										Add new integration
									</Typography>

									<Dropzone
										onDrop={acceptedFiles =>
											readTextFile(acceptedFiles)
										}>
										{({
											getRootProps,
											getInputProps,
											isDragActive,
										}) => (
											<Box
												{...getRootProps()}
												sx={{
													border: `2px dashed ${txtError.status ? 'red' : '#b3ede8'}`,
													borderRadius: '12px',
													p: '28px 20px',
													textAlign: 'center',
													background: isDragActive
														? '#d0f5f1'
														: '#e6f9f7',
													cursor: 'pointer',
													transition:
														'all 0.15s ease',
													'&:hover': {
														background: '#d0f5f1',
														borderColor: teal,
													},
												}}>
												<input {...getInputProps()} />

												<CloudUploadRoundedIcon
													sx={{
														fontSize: 32,
														color: teal,
														mb: 1,
													}}
												/>
												{!fileData ? (
													<>
														<Typography
															sx={{
																fontSize: 14,
																fontWeight: 600,
																color: dark,
																mb: 0.5,
															}}>
															Upload API config
															JSON
														</Typography>

														<Typography
															sx={{
																fontSize: 12,
																color: '#6b8888',
																lineHeight: 1.5,
															}}>
															Connect Care Vision,
															Arquella, Oomph!, or
															any custom system
														</Typography>
													</>
												) : (
													<Typography
														sx={{
															fontSize: 14,
															fontWeight: 600,
															color: dark,
															mb: 0.5,
														}}>
														{fileData.name}
													</Typography>
												)}
											</Box>
										)}
									</Dropzone>
									{txtError.status && (
										<Typography style={helperText}>
											{txtError.msg}
										</Typography>
									)}
								</Box>
								<Button
									onClick={onUploadJsonFile}
									variant="contained"
									disabled={isLoading}
									startIcon={<UploadFileOutlinedIcon />}
									sx={{
										width: '40%',
										borderRadius: '12px',
										backgroundColor: teal,
										textTransform: 'none',
										fontWeight: 600,
										fontSize: '13px',
										boxShadow: 'none',
										'&:hover': {
											backgroundColor: teal,
											boxShadow: 'none',
										},
										color: dark,
										marginTop: '10px',
									}}>
									Onboard
								</Button>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
			<ToastBanner
				open={isLoading}
				message={loadingMsg}
				icon={<AutoAwesomeIcon color={'#FFFFFF'} />}
				isCompleted={isGenerationComplete}
			/>
		</MainGrid>
	);
}
